import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  HandCoins, Calendar, CheckCircle, Clock,
  AlertTriangle, IndianRupee
} from 'lucide-react';
import MemberLayout from '../../components/layouts/MemberLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';

const MemberContributions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      const response = await apiClient.get('/dashboard/member-stats');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch contribution data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const contributions = data?.contributions || [];
  
  const stats = {
    total: contributions.length,
    paid: contributions.filter(c => c.status === 'paid').length,
    pending: contributions.filter(c => c.status === 'pending').length,
    late: contributions.filter(c => c.status === 'late').length,
    totalAmount: contributions.filter(c => ['paid', 'late'].includes(c.status)).reduce((s, c) => s + c.amount, 0),
    totalPenalties: contributions.reduce((s, c) => s + (c.penalty_amount || 0), 0)
  };

  // Find next pending contribution
  const nextPending = contributions.find(c => c.status === 'pending');

  return (
    <MemberLayout title="Contributions" subtitle="Your monthly contribution history">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-[#425466]">Paid</p>
          <p className="text-2xl font-bold text-green-600">{stats.paid}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
          <p className="text-sm text-[#425466]">Pending</p>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#00C853]/10 flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-[#00C853]" />
            </div>
          </div>
          <p className="text-sm text-[#425466]">Total Contributed</p>
          <p className="text-2xl font-bold text-[#00C853]">{formatCurrency(stats.totalAmount)}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="dashboard-card p-6"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
          </div>
          <p className="text-sm text-[#425466]">Total Penalties</p>
          <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalPenalties)}</p>
        </motion.div>
      </div>

      {/* Next Contribution Alert */}
      {nextPending && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="dashboard-card p-6 mb-8 border-l-4 border-[#2962FF]"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-[#2962FF]/10 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-[#2962FF]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#0A2540]">Next Contribution Due</h3>
                <p className="text-sm text-[#425466]">
                  For {nextPending.month} • Due by {new Date(nextPending.due_date).toLocaleDateString('en-IN')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#2962FF]">{formatCurrency(nextPending.amount)}</p>
              <p className="text-xs text-[#425466]">Monthly contribution</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Contribution History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Contribution History
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F4F7FB]">
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase">Month</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase">Due Date</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase">Amount</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[#425466] uppercase">Penalty</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-[#425466] uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[#425466] uppercase">Paid Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-20 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-16 rounded-full mx-auto"></div></td>
                    <td className="px-6 py-4"><div className="skeleton h-6 w-24 rounded"></div></td>
                  </tr>
                ))
              ) : contributions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#425466]">
                    <HandCoins className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>No contributions yet</p>
                  </td>
                </tr>
              ) : (
                contributions.map((contrib, index) => (
                  <tr 
                    key={contrib.id} 
                    className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <td className="px-6 py-4">
                      <span className="font-semibold text-[#0A2540]">{contrib.month}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#425466]" />
                        <span className="text-[#425466]">
                          {new Date(contrib.due_date).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className="font-semibold text-[#0A2540]">{formatCurrency(contrib.amount)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {contrib.penalty_amount > 0 ? (
                        <span className="text-red-600 font-medium">{formatCurrency(contrib.penalty_amount)}</span>
                      ) : (
                        <span className="text-[#425466]">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        contrib.status === 'paid' ? 'bg-green-100 text-green-600' :
                        contrib.status === 'late' ? 'bg-red-100 text-red-600' :
                        'bg-yellow-100 text-yellow-600'
                      }`}>
                        {contrib.status === 'paid' ? <CheckCircle className="w-3 h-3" /> :
                         contrib.status === 'late' ? <AlertTriangle className="w-3 h-3" /> :
                         <Clock className="w-3 h-3" />}
                        {contrib.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {contrib.paid_date ? (
                        <span className="text-[#425466]">
                          {new Date(contrib.paid_date).toLocaleDateString('en-IN')}
                        </span>
                      ) : (
                        <span className="text-[#425466]">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Info Box */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-6 p-6 rounded-xl bg-[#F4F7FB] border border-gray-200"
      >
        <h4 className="font-semibold text-[#0A2540] mb-3">Contribution Guidelines</h4>
        <ul className="space-y-2 text-sm text-[#425466]">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] mt-2"></span>
            <span>Monthly contribution amount is <strong>₹1,000</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] mt-2"></span>
            <span>Contributions are due on the <strong>4th Sunday</strong> of every month</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2"></span>
            <span>Late payment penalty: <strong>₹100 per day</strong></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2962FF] mt-2"></span>
            <span>Contact admin if you have any payment issues</span>
          </li>
        </ul>
      </motion.div>
    </MemberLayout>
  );
};

export default MemberContributions;
